import React, { useState, useEffect } from 'react';
import { Card, Form, Select, Switch, Button, message, Space, Divider, Input, TimePicker, Upload, Modal } from 'antd';
import { GlobalOutlined, SaveOutlined, DatabaseOutlined, MailOutlined, PictureOutlined, UploadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuthStore } from '../store/authStore';
import api from '../utils/api';
import config from '../config';
import dayjs from 'dayjs';

const { Option } = Select;

const Settings = () => {
    const { t, i18n } = useTranslation();
    const { user, updateUser } = useAuthStore();
    const [form] = Form.useForm();
    const [backupForm] = Form.useForm();
    const [logoForm] = Form.useForm();
    const queryClient = useQueryClient();
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [academyForm] = Form.useForm();

    // Fetch academy data
    const { data: academyData, refetch: refetchAcademy } = useQuery('academy-settings', async () => {
        try {
            const response = await api.get('/academy');
            return response.data.data;
        } catch (error) {
            return null;
        }
    });

    // Fetch backup settings
    const { data: backupSettings, refetch } = useQuery('backup-settings', async () => {
        try {
            const response = await api.get('/settings/backup');
            return response.data.data;
        } catch (error) {
            return null;
        }
    });

    const saveBackupMutation = useMutation(
        (data) => api.post('/settings/backup', data),
        {
            onSuccess: () => {
                message.success(t('backupSettingsSaved'));
                refetch();
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('failedToSaveBackupSettings'));
            },
        }
    );

    const testBackupMutation = useMutation(
        () => api.post('/settings/backup/test'),
        {
            onSuccess: () => {
                message.success(t('backupEmailSent'));
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('failedToSendBackup'));
            },
        }
    );

    const uploadLogoMutation = useMutation(
        async (file) => {
            const formData = new FormData();
            formData.append('logo', file);
            const response = await api.post('/settings/upload-logo', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return response.data.data;
        },
        {
            onSuccess: (academy) => {
                message.success(t('logoUploadedSuccessfully'));
                refetchAcademy();
                queryClient.invalidateQueries('auth');
                // Update user in authStore with new academy data
                if (user && academy) {
                    updateUser({ ...user, academy });
                }
                setLogoFile(null);
                setLogoPreview(null);
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('failedToUploadLogo'));
            },
        }
    );

    const removeLogoMutation = useMutation(
        () => api.delete('/settings/logo'),
        {
            onSuccess: async () => {
                message.success(t('logoRemovedSuccessfully'));
                const { data } = await refetchAcademy();
                queryClient.invalidateQueries('auth');
                // Update user in authStore with updated academy data (logo removed)
                if (user && data) {
                    updateUser({ ...user, academy: data });
                }
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('failedToRemoveLogo'));
            },
        }
    );

    const updateAcademyMutation = useMutation(
        (data) => api.put('/academy', data),
        {
            onSuccess: async () => {
                message.success(t('academyDetailsUpdated'));
                const { data } = await refetchAcademy();
                queryClient.invalidateQueries('auth');
                if (user && data) {
                    updateUser({ ...user, academy: data });
                }
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('failedToUpdateAcademy'));
            },
        }
    );

    const handleSave = (values) => {
        // Change language
        if (values.language) {
            i18n.changeLanguage(values.language);
            localStorage.setItem('language', values.language);
        }

        // Save other settings to localStorage
        localStorage.setItem('settings', JSON.stringify(values));

        message.success(t('settingsSaved'));
    };

    const handleBackupSave = (values) => {
        const backupData = {
            enabled: values.backupEnabled,
            email: values.backupEmail,
            time: values.backupTime ? values.backupTime.format('HH:mm') : '00:00',
        };
        saveBackupMutation.mutate(backupData);
    };

    const handleTestBackup = () => {
        testBackupMutation.mutate();
    };

    const handleLogoChange = (info) => {
        const file = info.file.originFileObj || info.file;
        if (file) {
            setLogoFile(file);
            const reader = new FileReader();
            reader.onload = (e) => setLogoPreview(e.target.result);
            reader.readAsDataURL(file);
        }
    };

    const handleLogoUpload = () => {
        if (logoFile) {
            uploadLogoMutation.mutate(logoFile);
        }
    };

    const handleLogoRemove = () => {
        Modal.confirm({
            title: t('confirmRemoveLogo'),
            content: t('areYouSureRemoveLogo'),
            onOk: () => removeLogoMutation.mutate(),
        });
    };

    const handleAcademyUpdate = (values) => {
        // Transform flat form values to nested structure
        const transformedData = {
            name: values.name,
            email: values.email,
            phone: values.phone,
            website: values.website,
            address: {
                street: values['address.street'],
                city: values['address.city'],
                state: values['address.state'],
                country: values['address.country'],
            },
            socialMedia: {
                facebook: values['socialMedia.facebook'],
                instagram: values['socialMedia.instagram'],
                twitter: values['socialMedia.twitter'],
                linkedin: values['socialMedia.linkedin'],
                youtube: values['socialMedia.youtube'],
                telegram: values['socialMedia.telegram'],
                whatsapp: values['socialMedia.whatsapp'],
            }
        };
        updateAcademyMutation.mutate(transformedData);
    };

    // Update form when academy data loads
    useEffect(() => {
        if (academyData) {
            academyForm.setFieldsValue({
                name: academyData.name || '',
                email: academyData.email || '',
                phone: academyData.phone || '',
                website: academyData.website || '',
                'address.street': academyData.address?.street || '',
                'address.city': academyData.address?.city || '',
                'address.state': academyData.address?.state || '',
                'address.country': academyData.address?.country || '',
                'socialMedia.facebook': academyData.socialMedia?.facebook || '',
                'socialMedia.instagram': academyData.socialMedia?.instagram || '',
                'socialMedia.twitter': academyData.socialMedia?.twitter || '',
                'socialMedia.linkedin': academyData.socialMedia?.linkedin || '',
                'socialMedia.youtube': academyData.socialMedia?.youtube || '',
                'socialMedia.telegram': academyData.socialMedia?.telegram || '',
                'socialMedia.whatsapp': academyData.socialMedia?.whatsapp || '',
            });
        }
    }, [academyData, academyForm]);

    // Load saved settings
    const savedSettings = JSON.parse(localStorage.getItem('settings') || '{}');
    const currentLanguage = localStorage.getItem('language') || 'en';

    return (
        <div>
            <h1 style={{ marginBottom: 24 }}>{t('settings')}</h1>

            <Card style={{ maxWidth: 800, marginBottom: 24 }}>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSave}
                    initialValues={{
                        language: currentLanguage,
                    }}
                >
                    <h3>
                        <GlobalOutlined /> {t('languageSettings')}
                    </h3>
                    <Form.Item
                        name="language"
                        label={t('language')}
                        help={t('selectYourPreferredLanguage')}
                    >
                        <Select style={{ width: 200 }}>
                            <Option value="en">English</Option>
                            <Option value="ar">العربية (Arabic)</Option>
                            <Option value="ku">کوردی (Kurdish)</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button
                                type="primary"
                                htmlType="submit"
                                icon={<SaveOutlined />}
                            >
                                {t('saveSettings')}
                            </Button>
                            <Button onClick={() => form.resetFields()}>
                                {t('reset')}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Card>

            <Card style={{ maxWidth: 800, marginBottom: 24 }}>
                <Form
                    form={academyForm}
                    layout="vertical"
                    onFinish={handleAcademyUpdate}
                    initialValues={{
                        name: academyData?.name || '',
                        email: academyData?.email || '',
                        phone: academyData?.phone || '',
                        website: academyData?.website || '',
                        'address.street': academyData?.address?.street || '',
                        'address.city': academyData?.address?.city || '',
                        'address.state': academyData?.address?.state || '',
                        'address.country': academyData?.address?.country || '',
                        'socialMedia.facebook': academyData?.socialMedia?.facebook || '',
                        'socialMedia.instagram': academyData?.socialMedia?.instagram || '',
                        'socialMedia.twitter': academyData?.socialMedia?.twitter || '',
                        'socialMedia.linkedin': academyData?.socialMedia?.linkedin || '',
                        'socialMedia.youtube': academyData?.socialMedia?.youtube || '',
                        'socialMedia.telegram': academyData?.socialMedia?.telegram || '',
                        'socialMedia.whatsapp': academyData?.socialMedia?.whatsapp || '',
                    }}
                >
                    <h3>
                        <GlobalOutlined /> {t('academyDetails')}
                    </h3>
                    <p style={{ color: '#666', marginBottom: 24 }}>
                        {t('updateAcademyInformation')}
                    </p>

                    <Form.Item
                        name="name"
                        label={t('academyName')}
                        rules={[{ required: true, message: t('pleaseEnterAcademyName') }]}
                    >
                        <Input size="large" placeholder={t('academyName')} />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        label={t('email')}
                        rules={[
                            { required: true, message: t('pleaseEnterEmail') },
                            { type: 'email', message: t('pleaseEnterValidEmail') }
                        ]}
                    >
                        <Input size="large" placeholder="academy@example.com" />
                    </Form.Item>

                    <Form.Item
                        name="phone"
                        label={t('phone')}
                        rules={[{ required: true, message: t('pleaseEnterPhone') }]}
                    >
                        <Input size="large" placeholder="+1234567890" />
                    </Form.Item>

                    <Form.Item name="website" label={t('website')}>
                        <Input size="large" placeholder="https://academy.com" />
                    </Form.Item>

                    <Divider>{t('address')}</Divider>

                    <Form.Item name="address.street" label={t('street')}>
                        <Input size="large" placeholder={t('street')} />
                    </Form.Item>

                    <Form.Item name="address.city" label={t('city')}>
                        <Input size="large" placeholder={t('city')} />
                    </Form.Item>

                    <Form.Item name="address.state" label={t('state')}>
                        <Input size="large" placeholder={t('state')} />
                    </Form.Item>

                    <Form.Item name="address.country" label={t('country')}>
                        <Input size="large" placeholder={t('country')} />
                    </Form.Item>

                    <Divider>{t('socialMedia')}</Divider>
                    <p style={{ color: '#666', marginBottom: 16 }}>
                        {t('socialMediaDescription')}
                    </p>

                    <Form.Item name="socialMedia.facebook" label="Facebook">
                        <Input size="large" prefix="@" placeholder="facebook_username" />
                    </Form.Item>

                    <Form.Item name="socialMedia.instagram" label="Instagram">
                        <Input size="large" prefix="@" placeholder="instagram_username" />
                    </Form.Item>

                    <Form.Item name="socialMedia.twitter" label="Twitter / X">
                        <Input size="large" prefix="@" placeholder="twitter_username" />
                    </Form.Item>

                    <Form.Item name="socialMedia.linkedin" label="LinkedIn">
                        <Input size="large" placeholder="company-name" />
                    </Form.Item>

                    <Form.Item name="socialMedia.youtube" label="YouTube">
                        <Input size="large" prefix="@" placeholder="youtube_channel" />
                    </Form.Item>

                    <Form.Item name="socialMedia.telegram" label="Telegram">
                        <Input size="large" prefix="@" placeholder="telegram_username" />
                    </Form.Item>

                    <Form.Item name="socialMedia.whatsapp" label="WhatsApp">
                        <Input size="large" placeholder="+1234567890" />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button
                                type="primary"
                                htmlType="submit"
                                icon={<SaveOutlined />}
                                loading={updateAcademyMutation.isLoading}
                            >
                                {t('saveChanges')}
                            </Button>
                            <Button onClick={() => academyForm.resetFields()}>
                                {t('reset')}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Card>

            <Card style={{ maxWidth: 800, marginBottom: 24 }}>
                <Form layout="vertical">
                    <h3>
                        <PictureOutlined /> {t('academyLogo')}
                    </h3>
                    <p style={{ color: '#666', marginBottom: 24 }}>
                        {t('logoDescription')}
                    </p>

                    {academyData?.logo && (
                        <div style={{ marginBottom: 24 }}>
                            <div style={{ marginBottom: 8, fontWeight: 600 }}>{t('currentLogo')}</div>
                            <div style={{
                                border: '2px solid #f0f0f0',
                                borderRadius: '8px',
                                padding: '20px',
                                display: 'inline-block',
                                background: '#fafafa'
                            }}>
                                <img
                                    src={`${academyData.logo}`}
                                    alt="Academy Logo"
                                    style={{
                                        maxHeight: '120px',
                                        maxWidth: '300px',
                                        objectFit: 'contain',
                                        display: 'block'
                                    }}
                                />
                            </div>
                            <div style={{ marginTop: 12 }}>
                                <Button
                                    danger
                                    onClick={handleLogoRemove}
                                    loading={removeLogoMutation.isLoading}
                                >
                                    {t('removeLogo')}
                                </Button>
                            </div>
                        </div>
                    )}

                    <Form.Item
                        label={academyData?.logo ? t('uploadNewLogo') : t('uploadLogo')}
                        help={t('logoUploadHelp')}
                    >
                        <Upload
                            accept="image/*"
                            beforeUpload={() => false}
                            onChange={handleLogoChange}
                            maxCount={1}
                            showUploadList={false}
                        >
                            <Button icon={<UploadOutlined />} size="large">
                                {t('selectFile')}
                            </Button>
                        </Upload>
                    </Form.Item>

                    {logoPreview && (
                        <div style={{ marginTop: 16, marginBottom: 24 }}>
                            <div style={{ marginBottom: 8, fontWeight: 600 }}>{t('preview')}</div>
                            <div style={{
                                border: '2px dashed #d9d9d9',
                                borderRadius: '8px',
                                padding: '20px',
                                display: 'inline-block',
                                background: '#fafafa'
                            }}>
                                <img
                                    src={logoPreview}
                                    alt="Logo Preview"
                                    style={{
                                        maxHeight: '120px',
                                        maxWidth: '300px',
                                        objectFit: 'contain',
                                        display: 'block'
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {logoFile && (
                        <Form.Item>
                            <Space>
                                <Button
                                    type="primary"
                                    icon={<UploadOutlined />}
                                    onClick={handleLogoUpload}
                                    loading={uploadLogoMutation.isLoading}
                                >
                                    {t('uploadLogo')}
                                </Button>
                                <Button onClick={() => {
                                    setLogoFile(null);
                                    setLogoPreview(null);
                                }}>
                                    {t('cancel')}
                                </Button>
                            </Space>
                        </Form.Item>
                    )}
                </Form>
            </Card>

            <Card style={{ maxWidth: 800 }}>
                <Form
                    form={backupForm}
                    layout="vertical"
                    onFinish={handleBackupSave}
                    initialValues={{
                        backupEnabled: backupSettings?.enabled || false,
                        backupEmail: backupSettings?.email || '',
                        backupTime: backupSettings?.time ? dayjs(backupSettings.time, 'HH:mm') : dayjs('00:00', 'HH:mm'),
                    }}
                >
                    <h3>
                        <DatabaseOutlined /> {t('automaticDatabaseBackup')}
                    </h3>
                    <p style={{ color: '#666', marginBottom: 24 }}>
                        {t('backupDescription')}
                    </p>

                    <Form.Item
                        name="backupEnabled"
                        label={t('enableAutomaticBackup')}
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>

                    <Form.Item
                        name="backupEmail"
                        label={t('backupEmail')}
                        rules={[
                            { type: 'email', message: t('pleaseEnterValidEmail') },
                            { required: true, message: t('pleaseEnterBackupEmail') }
                        ]}
                        help={t('backupEmailHelp')}
                    >
                        <Input
                            prefix={<MailOutlined />}
                            placeholder="admin@example.com"
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item
                        name="backupTime"
                        label={t('backupTime')}
                        help={t('backupTimeHelp')}
                    >
                        <TimePicker
                            format="HH:mm"
                            size="large"
                            style={{ width: 200 }}
                        />
                    </Form.Item>

                    <Divider />

                    <Form.Item>
                        <Space>
                            <Button
                                type="primary"
                                htmlType="submit"
                                icon={<SaveOutlined />}
                                loading={saveBackupMutation.isLoading}
                            >
                                {t('saveBackupSettings')}
                            </Button>
                            <Button
                                onClick={handleTestBackup}
                                loading={testBackupMutation.isLoading}
                                icon={<MailOutlined />}
                            >
                                {t('sendTestBackup')}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
};

export default Settings;
